/**
 * Same-origin BFF transport for the SPA.
 *
 * Every backend call in the portal goes through here. Three things
 * this owns that the bare `fetch` doesn't:
 *
 *   1. Always sends the HttpOnly session cookie (`credentials: "include"`).
 *      The cookie is set by the BFF Lambdas (`amplify/functions/auth-*`)
 *      and the SPA never reads or writes it directly — that's the
 *      whole point of the architecture (SECURITY-AUDIT C-1 closed).
 *
 *   2. Same-origin guard: any URL that doesn't start with `/api/`
 *      throws. Cross-origin calls bypass the cookie + CSP boundary;
 *      they should go through the BFF, not the SPA.
 *
 *   3. 401 → silent refresh → retry. If a request comes back 401,
 *      we POST `/api/auth/refresh` once. On success we retry the
 *      original request. On failure we fire `auth:logout` so the
 *      AuthProvider can navigate to `/`.
 *
 *      A single in-flight refresh promise dedupes the case where
 *      multiple concurrent 401s arrive (e.g. parallel data fetches
 *      on a route). The first 401 starts the refresh; subsequent
 *      401s within the same window await the same promise.
 *
 *   4. Non-2xx → throws an `ApiError` with the parsed JSON body.
 *      TanStack Query catches it as the mutation/query error.
 */

export class ApiError extends Error {
  status: number;
  body: { error?: string; message?: string; status?: number } | null;

  constructor(message: string, status: number, body: ApiError["body"]) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

/**
 * Logout broadcast — `AuthProvider` listens for this on `window` and
 * navigates to `/` when it fires. Decoupled via event so apiFetch
 * doesn't need to import the navigation context (apiFetch can be
 * called from anywhere, including outside the React tree).
 */
const AUTH_LOGOUT_EVENT = "auth:logout";

let refreshInFlight: Promise<boolean> | null = null;

const refresh = async (): Promise<boolean> => {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const response = await fetch("/api/auth/refresh", {
        method: "POST",
        credentials: "include",
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });
      return response.ok;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
};

const broadcastLogout = (): void => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(AUTH_LOGOUT_EVENT));
  }
};

const buildHeaders = (
  init: RequestInit | undefined,
  body: BodyInit | null | undefined,
): HeadersInit => {
  const headers = new Headers(init?.headers);
  if (!headers.has("X-Requested-With")) {
    headers.set("X-Requested-With", "XMLHttpRequest");
  }
  // JSON content-type defaulted when caller passes a string body.
  // FormData / Blob / URLSearchParams keep their auto-set content-type.
  if (
    typeof body === "string" &&
    !headers.has("Content-Type") &&
    init?.method &&
    init.method.toUpperCase() !== "GET"
  ) {
    headers.set("Content-Type", "application/json");
  }
  return headers;
};

/**
 * Internal: one fetch attempt. The 401-refresh-retry orchestration
 * happens in the public `apiFetch` below.
 */
const doFetch = async (
  path: string,
  init: RequestInit | undefined,
): Promise<Response> => {
  const headers = buildHeaders(init, init?.body);
  return fetch(path, {
    ...init,
    headers,
    credentials: "include",
  });
};

/**
 * Send a request to the BFF. Returns the raw Response — caller
 * decides how to parse (use `apiFetchJson` for the common JSON case).
 *
 * Throws `ApiError` on non-2xx response.
 */
export const apiFetch = async (
  path: string,
  init?: RequestInit,
): Promise<Response> => {
  if (!path.startsWith("/api/")) {
    throw new Error(
      `apiFetch: path must start with /api/ (got: ${path}). Cross-origin calls bypass the cookie + CSP boundary; route them through the BFF instead.`,
    );
  }

  let response = await doFetch(path, init);

  // 401 → refresh once, retry once.
  //
  // **Why a successful refresh + retry-401 must NOT broadcast logout
  // (caught 2026-05-22):** a successful `/api/auth/refresh` proves
  // the session is alive — Cognito accepted the refresh token and
  // re-minted the cookie. If the retried resource call still 401s,
  // the failure is at the resource layer (the customer's account
  // is not provisioned for the env / scope the request was made
  // against, e.g. switching the env switcher to "development" when
  // their plan only covers sandbox). Logging the user out of the
  // app for "you can't use that environment" is the wrong response
  // and was kicking customers to /home on every env-switch.
  //
  // After refresh+retry-401: surface the ApiError to the caller
  // (the per-feature query renders ViewErrorRetry, the customer can
  // switch the env switcher back, no auth state lost).
  if (response.status === 401 && path !== "/api/auth/refresh") {
    const refreshed = await refresh();
    if (refreshed) {
      response = await doFetch(path, init);
      // Intentionally no broadcastLogout() on retry-401 — see comment above.
    } else {
      broadcastLogout();
    }
  }

  if (!response.ok) {
    let body: ApiError["body"] = null;
    try {
      body = await response.json();
    } catch {
      // not JSON — keep null
    }
    const message =
      body?.error ||
      body?.message ||
      `${response.status} ${response.statusText}`;
    throw new ApiError(message, response.status, body);
  }

  return response;
};

/**
 * Send a request and parse the response as JSON. Almost every BFF
 * endpoint returns JSON; this is the convenience wrapper.
 */
export const apiFetchJson = async <T>(
  path: string,
  init?: RequestInit,
): Promise<T> => {
  const response = await apiFetch(path, init);
  return (await response.json()) as T;
};
